import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { notifications } from '@mantine/notifications';
import type { ItemsReportRow, ItemsReportView } from 'shared-models/browser';
import { reportsService } from '../../../../../services/reports/reports.service';
import { productsService, type ProductView } from '../../../../../services/products/products.service';
import { categoriesService, type CategoryView } from '../../../../../services/categories/categories.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { formatMoney, isolateAmountLtr, roundMoney } from '../../../../../utils/money';
import {
  ITEMS_REPORT_PRESETS,
  formatRangeLabel,
  rangeForPreset,
  type ItemsReportPreset,
} from './items-report-range.util';
import classes from './pos-items-report.module.scss';

export type PosItemsReportModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
};

// iOS types a curly ’ where Android types ', and Hebrew names mix those with the geresh ׳.
// Comparing letters and digits only makes every spelling of צ'ייסר match the same product.
const searchable = (value: string): string => value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

// Mobile card cell: the table's column name paired with its value.
const Cell: React.FC<{ label: string; value: React.ReactNode; tone?: 'danger' }> = ({ label, value, tone }) => (
  <div className={classes.cardCell}>
    <span className={classes.cardCellLabel}>{label}</span>
    <strong className={tone === 'danger' ? `${classes.cardCellValue} ${classes.canceledCell}` : classes.cardCellValue}>
      {value}
    </strong>
  </div>
);

// Summary: דוח פריטים — date range + all/specific products, then a per-product sales table.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
const PosItemsReportModalComponent: React.FC<PosItemsReportModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
}) => {
  const [preset, setPreset] = useState<ItemsReportPreset>('today');
  const [range, setRange] = useState(() => rangeForPreset('today'));
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState<ProductView[]>([]);
  const [categories, setCategories] = useState<CategoryView[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<number[]>([]);
  const [report, setReport] = useState<ItemsReportView | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const stripRef = useRef<HTMLElement | null>(null);
  const [scrollHint, setScrollHint] = useState({ start: false, end: false });

  // RTL browsers report scrollLeft as negative, so the distance travelled is its magnitude.
  const syncScrollHint = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) {
      return;
    }
    const travelled = Math.abs(strip.scrollLeft);
    const maxTravel = strip.scrollWidth - strip.clientWidth;
    setScrollHint({ start: travelled > 1, end: travelled < maxTravel - 1 });
  }, []);

  // Filters reset on close, so an open always starts from היום / כל הפריטים and the auto-run
  // below fires exactly once with those defaults instead of twice around a reset.
  useEffect(() => {
    if (opened) {
      return;
    }
    setPreset('today');
    setRange(rangeForPreset('today'));
    setProductSearch('');
    setCategoryFilter([]);
    setReport(null);
    setLoading(false);
  }, [opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }
    void productsService
      .listForSettings(tenantId, employeeSession)
      .then((rows) => setProducts(rows.filter((product) => product.isActive)))
      .catch(() => setProducts([]));
    // Inactive categories stay in the map so an active product still resolves its group name.
    void categoriesService
      .listForSettings(tenantId, employeeSession)
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [opened, tenantId, employeeSession]);

  const handlePreset = (next: ItemsReportPreset) => {
    // Re-picking the active preset produced identical range strings, so the cleared report had
    // nothing to react to and the table stayed blank. The shown report is already its answer.
    if (next === preset) {
      return;
    }
    setPreset(next);
    setReport(null);
    if (next !== 'custom') {
      setRange(rangeForPreset(next));
    }
  };

  const canRun = range.from !== '' && range.to !== '' && range.from <= range.to;

  const runReport = useCallback(async () => {
    // Typing in the date inputs re-fires this, so a superseded response must never win.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    try {
      const data = await reportsService.getItemsReport(
        tenantId,
        {
          // היום follows the live shift, not the calendar: the backend resolves the open shift,
          // today's shifts, or the last shift.
          ...(preset === 'today'
            ? { scope: 'today' as const }
            : { scope: 'range' as const, from: range.from, to: range.to }),
        },
        employeeSession,
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      setReport(data);
    } catch (error: unknown) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'הפקת הדוח נכשלה',
        autoClose: 3000,
      });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [employeeSession, preset, range.from, range.to, tenantId]);

  // Every preset, date and scope change reruns the report on its own — there is no run button.
  useEffect(() => {
    if (!opened || !canRun) {
      return;
    }
    void runReport();
  }, [canRun, opened, runReport]);

  // A deal is a container, not a sold unit: rows with combo components contribute their components'
  // quantities and prorated prices instead of their own, so filtering down to a component totals
  // only that component's share of the deal.
  const stripCategories = useMemo(
    () => categories.filter((category) => products.some((product) => product.categoryId === category.id)),
    [categories, products],
  );

  // Without this the end arrow would only appear after the first manual scroll.
  useEffect(() => {
    syncScrollHint();
    window.addEventListener('resize', syncScrollHint);
    return () => window.removeEventListener('resize', syncScrollHint);
  }, [syncScrollHint, opened, stripCategories.length]);

  const visibleRows = useMemo(() => {
    const rows = report?.rows ?? [];
    if (rows.length === 0) {
      return rows;
    }
    const inCategory = categoryFilter.length === 0
      ? null
      : new Set(products
        .filter((product) => product.categoryId !== null && categoryFilter.includes(product.categoryId))
        .map((product) => product.id));
    const term = searchable(productSearch);
    return rows.filter((row) => {
      if (inCategory && !inCategory.has(row.productId)) {
        return false;
      }
      if (!term) {
        return true;
      }
      // Sizes ride on variantName and deal components on comboItems, so one box covers all three.
      return searchable(row.productName).includes(term)
        || searchable(row.variantName ?? '').includes(term)
        || row.comboItems.some((item) => searchable(item.itemName).includes(term));
    });
  }, [report, categoryFilter, products, productSearch]);

  const totals = useMemo(() => {
    if (visibleRows.length === 0) {
      return null;
    }
    const countable = (
      row: ItemsReportRow,
      pick: (source: { soldQuantity: number; canceledQuantity: number; salesAmount: number }) => number,
    ) => (
      row.comboItems.length > 0
        ? row.comboItems.reduce((sum, item) => sum + pick(item), 0)
        : pick(row)
    );
    const soldQuantity = visibleRows.reduce((sum, row) => sum + countable(row, (source) => source.soldQuantity), 0);
    const salesAmount = roundMoney(visibleRows.reduce((sum, row) => sum + countable(row, (source) => source.salesAmount), 0));
    const canceledQuantity = visibleRows.reduce((sum, row) => sum + countable(row, (source) => source.canceledQuantity), 0);
    return {
      soldQuantity,
      salesAmount,
      canceledQuantity,
      avgActualPrice: soldQuantity > 0 ? roundMoney(salesAmount / soldQuantity) : 0,
      salesSharePercent: report && report.rangeSalesTotal > 0
        ? roundMoney((salesAmount / report.rangeSalesTotal) * 100, false)
        : 0,
    };
  }, [report, visibleRows]);


  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      fullScreen
      padding={0}
      radius={0}
      zIndex={400}
      classNames={{ body: classes.modalBody }}
    >
      <div className={classes.shell} dir="rtl" data-testid="items-report-modal">
        <div className={classes.topBar}>
          <span className={classes.topTitle}>דוח פריטים</span>
          <UnstyledButton
            component="button"
            type="button"
            data-testid="items-report-close"
            className={classes.iconButton}
            onClick={onClose}
            aria-label="סגור"
          >
            <FontAwesomeIcon icon={faXmark as IconProp} />
          </UnstyledButton>
        </div>

        <div className={classes.body}>
          <div className={classes.page}>
            <section className={classes.card}>
              <div className={classes.cardLabel}>טווח תאריכים</div>
              <div className={classes.presetRow}>
                {ITEMS_REPORT_PRESETS.map((option) => (
                  <UnstyledButton
                    key={option.value}
                    component="button"
                    type="button"
                    data-testid={`items-report-preset-${option.value}`}
                    aria-pressed={preset === option.value}
                    className={preset === option.value ? `${classes.preset} ${classes.presetActive}` : classes.preset}
                    onClick={() => handlePreset(option.value)}
                  >
                    {option.label}
                  </UnstyledButton>
                ))}
              </div>
              {preset === 'custom' ? (
                <div className={classes.rangeRow}>
                  <TextInput
                    type="date"
                    label="מתאריך"
                    data-testid="items-report-from"
                    value={range.from}
                    onChange={(event) => {
                      const from = event.currentTarget.value;
                      setRange((current) => ({ ...current, from }));
                      setReport(null);
                    }}
                  />
                  <TextInput
                    type="date"
                    label="עד תאריך"
                    data-testid="items-report-to"
                    value={range.to}
                    onChange={(event) => {
                      const to = event.currentTarget.value;
                      setRange((current) => ({ ...current, to }));
                      setReport(null);
                    }}
                  />
                </div>
              ) : (
                <div className={classes.rangeHint} data-testid="items-report-range-hint">
                  {`משמרות שנפתחו ב-${formatRangeLabel(range.from, range.to)}`}
                </div>
              )}
            </section>

            <section className={classes.card}>
              <div className={classes.cardLabel}>היקף הדוח</div>
              <div className={classes.filterRow}>
                <TextInput
                  className={classes.filterSearch}
                  data-testid="items-report-search"
                  placeholder="חפש מוצר"
                  value={productSearch}
                  onChange={(event) => setProductSearch(event.currentTarget.value)}
                  leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
                />
                <UnstyledButton
                  component="button"
                  type="button"
                  data-testid="items-report-reset"
                  className={classes.filterReset}
                  onClick={() => {
                    setProductSearch('');
                    setCategoryFilter([]);
                  }}
                >
                  איפוס
                </UnstyledButton>
              </div>

              {stripCategories.length > 0 && (
                <>
                  <div className={classes.cardLabel}>קטגוריות</div>
                  <div className={classes.categoryStripRow}>
                  <span
                    aria-hidden="true"
                    data-testid="items-report-scroll-start"
                    className={scrollHint.start ? `${classes.scrollHint} ${classes.scrollHintOn}` : classes.scrollHint}
                  >
                    <FontAwesomeIcon icon={faChevronRight as IconProp} />
                  </span>
                  <nav
                    ref={stripRef}
                    onScroll={syncScrollHint}
                    className={classes.categoryStrip}
                    aria-label="קטגוריות"
                    data-testid="items-report-category-strip"
                  >
                    {stripCategories.map((category) => {
                      const on = categoryFilter.includes(category.id);
                      return (
                        <UnstyledButton
                          key={category.id}
                          component="button"
                          type="button"
                          aria-pressed={on}
                          data-testid={`items-report-category-${category.id}`}
                          className={on ? `${classes.categoryChip} ${classes.categoryChipActive}` : classes.categoryChip}
                          onClick={() => setCategoryFilter((current) => (
                            on ? current.filter((id) => id !== category.id) : [...current, category.id]
                          ))}
                        >
                          {category.name}
                        </UnstyledButton>
                      );
                    })}
                  </nav>
                  <span
                    aria-hidden="true"
                    data-testid="items-report-scroll-end"
                    className={scrollHint.end ? `${classes.scrollHint} ${classes.scrollHintOn}` : classes.scrollHint}
                  >
                    <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                  </span>
                  </div>
                </>
              )}
            </section>

            {loading && <div className={classes.center}><Loader /></div>}

            {!loading && report && (
              visibleRows.length === 0 ? (
                <div className={classes.empty} data-testid="items-report-empty">אין נתוני מכירות בטווח שנבחר</div>
              ) : (
                <>
                  <section className={classes.tableCard} data-testid="items-report-table">
                    <div className={classes.tableScroll}>
                      <table className={classes.table}>
                        <thead>
                          <tr>
                            <th>שם פריט</th>
                            <th>כמות שנמכרה</th>
                            <th>מחיר ממוצע בפועל</th>
                            <th>סה״כ מכירות</th>
                            <th>% מהמכירות</th>
                            <th>כמות שבוטלה</th>
                          </tr>
                          {totals && (
                            <tr className={classes.totalsRow} data-testid="items-report-totals">
                              <th className={classes.nameCell}>סה״כ</th>
                              <th>{totals.soldQuantity}</th>
                              <th><Money value={totals.avgActualPrice} /></th>
                              <th><Money value={totals.salesAmount} /></th>
                              <th>{`${totals.salesSharePercent}%`}</th>
                              <th>{totals.canceledQuantity}</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {visibleRows.map((row: ItemsReportRow) => {
                            const rowKey = `${row.productId}-${row.variantItemId ?? 'base'}`;
                            return (
                              <React.Fragment key={rowKey}>
                                <tr data-testid={`items-report-row-${rowKey}`}>
                                  <td className={classes.nameCell}>
                                    {row.productName}
                                    {row.variantName && <span className={classes.variantTag}>{row.variantName}</span>}
                                  </td>
                                  <td>{row.soldQuantity}</td>
                                  <td><Money value={row.avgActualPrice} /></td>
                                  <td><Money value={row.salesAmount} /></td>
                                  <td>{`${row.salesSharePercent}%`}</td>
                                  <td className={row.canceledQuantity > 0 ? classes.canceledCell : undefined}>{row.canceledQuantity}</td>
                                </tr>
                                {row.comboItems.map((item, index) => (
                                  <tr
                                    key={`${rowKey}-${item.itemId ?? `name-${index}`}`}
                                    className={classes.comboRow}
                                    data-testid={`items-report-combo-row-${rowKey}-${item.itemId ?? index}`}
                                  >
                                    <td className={classes.comboNameCell}>
                                      <span className={classes.comboArrow} aria-hidden="true">↳</span>
                                      {item.itemName}
                                    </td>
                                    <td>{item.soldQuantity}</td>
                                    <td><Money value={item.avgActualPrice} /></td>
                                    <td>
                                      <span className={classes.comboWithin}>מתוך</span>
                                      <Money value={item.salesAmount} />
                                    </td>
                                    <td className={classes.comboMuted}>—</td>
                                    <td className={item.canceledQuantity > 0 ? classes.canceledCell : undefined}>{item.canceledQuantity}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <div className={classes.cardList} data-testid="items-report-cards">
                    {totals && (
                      <article className={`${classes.itemCard} ${classes.totalsCard}`} data-testid="items-report-totals-card">
                        <div className={classes.cardTitle}>סה״כ</div>
                        <div className={classes.cardCells}>
                          <Cell label="כמות שנמכרה" value={totals.soldQuantity} />
                          <Cell label="מחיר ממוצע בפועל" value={<Money value={totals.avgActualPrice} />} />
                          <Cell label="סה״כ מכירות" value={<Money value={totals.salesAmount} />} />
                          <Cell label="% מהמכירות" value={`${totals.salesSharePercent}%`} />
                          <Cell label="כמות שבוטלה" value={totals.canceledQuantity} />
                        </div>
                      </article>
                    )}

                    {visibleRows.map((row: ItemsReportRow) => {
                      const rowKey = `${row.productId}-${row.variantItemId ?? 'base'}`;
                      return (
                        <article key={rowKey} className={classes.itemCard} data-testid={`items-report-card-${rowKey}`}>
                          <div className={classes.cardTitle}>
                            {row.productName}
                            {row.variantName && <span className={classes.variantTag}>{row.variantName}</span>}
                          </div>
                          <div className={classes.cardCells}>
                            <Cell label="כמות שנמכרה" value={row.soldQuantity} />
                            <Cell label="מחיר ממוצע בפועל" value={<Money value={row.avgActualPrice} />} />
                            <Cell label="סה״כ מכירות" value={<Money value={row.salesAmount} />} />
                            <Cell label="% מהמכירות" value={`${row.salesSharePercent}%`} />
                            <Cell
                              label="כמות שבוטלה"
                              value={row.canceledQuantity}
                              tone={row.canceledQuantity > 0 ? 'danger' : undefined}
                            />
                          </div>
                          {row.comboItems.length > 0 && (
                            <div className={classes.cardCombos}>
                              {row.comboItems.map((item, index) => (
                                <div key={`${rowKey}-${item.itemId ?? `name-${index}`}`} className={classes.cardComboRow}>
                                  <span className={classes.cardComboName}>
                                    <span className={classes.comboArrow} aria-hidden="true">↳</span>
                                    {item.itemName}
                                  </span>
                                  <span className={classes.cardComboMeta}>
                                    <Money value={item.salesAmount} />
                                    <span className={classes.comboWithin}>מתוך</span>
                                    {item.soldQuantity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const PosItemsReportModal = React.memo(PosItemsReportModalComponent);
